import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../firebase/AuthProvider";
import { Conversation } from "../../../services/chatService/ChatService";
import { getConversationById } from "../../../services/chatService/ChatService";
import { selectedConversation$ } from "../../../services/Subjects/ChatSubjects";
import ConversationList, { ConversationListRef } from "./ConversationListComponent";
import readchatimg from "../../../assets/images/readchatimg.svg";
import './chat.css';
import GlobalChatWindow from "./ChatWindow";
import { currentConversationId$ } from "../../../services/Subjects/userSubject";
import { useNavigate, useParams } from "react-router-dom";

interface ChatPageProps {
    isSideView?: boolean;
    handleShowChatHead?: (show: boolean) => void;
    showHeader?: boolean;
    handleCloseChat?: () => void;
    addConversationButtonRef?: React.RefObject<HTMLButtonElement>;
    handleToggleAddConversation?: () => void;
}

const ChatPage = React.forwardRef<ConversationListRef, ChatPageProps>((props, ref) => {
    const { isSideView = false, handleShowChatHead, showHeader = true, handleCloseChat,
        addConversationButtonRef, handleToggleAddConversation } = props;
    const { currentUser } = useAuth();
    const { conversationId } = useParams<{ conversationId?: string }>();
    const navigate = useNavigate();

    // State management for the page
    const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
    const [authToken, setAuthToken] = useState<string>("");
    const [isChatOpen, setIsChatOpen] = useState<boolean>(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // --- Handlers ---
    const handleChatOpen = useCallback(() => setIsChatOpen(true), []);

    const handleSelectConversation = useCallback((conversation: Conversation, doNavigate = true) => {
        // set to behavioural subject
        currentConversationId$.next(conversation.id);
        setSelectedConversation(conversation);
        if (doNavigate && !isSideView) {
            navigate(`/chat/${conversation.id}`);
        }
        handleChatOpen(); // Open the chat window when a conversation is selected
        if (handleShowChatHead) {
            handleShowChatHead(false);
        }
    }, [navigate, handleChatOpen, handleShowChatHead, isSideView]);

    const handleChatClose = useCallback(() => {
        setIsChatOpen(false);
        setSelectedConversation(null); // Clear selection when closing on mobile
        if (handleShowChatHead) {
            handleShowChatHead(true);
        }
        if (!isSideView) {
            navigate('/chat');
        }
    }, [navigate, handleShowChatHead, isSideView]);



    // --- Effects ---

    // Effect to handle responsive layout for mobile view
    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 767px)');
        const handleResize = () => setIsMobile(mediaQuery.matches);
        mediaQuery.addEventListener('change', handleResize);
        return () => mediaQuery.removeEventListener('change', handleResize);
    }, []);

    // Effect to get the Firebase auth token when the user is available
    useEffect(() => {
        if (currentUser) {
            currentUser.getIdToken().then(setAuthToken);
        }
    }, [currentUser]);

    useEffect(() => {
        if (conversationId && authToken) {
            if (selectedConversation?.id.toString() === conversationId) return;
            getConversationById(authToken, parseInt(conversationId)).then(conversation => {
                if (conversation) {
                    handleSelectConversation(conversation, false);
                }
            });
        }
    }, [conversationId, authToken, selectedConversation?.id, handleSelectConversation]);

    useEffect(() => {
        const subscription = selectedConversation$.subscribe(conversationId => {
            if (conversationId && authToken) {
                getConversationById(authToken, conversationId).then(conversation => {
                    if (conversation) {
                        handleSelectConversation(conversation);
                    }
                });
            }
        });
        return () => subscription.unsubscribe();
    }, [authToken, handleSelectConversation]);

    // Derived State
    const userId = currentUser?.uid;
    const singlePane = isMobile || isSideView; // Determines if we should show one or two panes

    const removeActiveMeetingStatusFromSelectedConversation = () => {
        if (selectedConversation) {
            setSelectedConversation({ ...selectedConversation, activeMeeting: undefined });
        }
    }

    return (
        <div className="chat_wrapper">
            {/* --- Conversation List (Left Pane) --- */}
            {authToken && userId && (
                <div
                    className="chat_wrapper_left h-100"
                    style={{
                        display: singlePane && isChatOpen ? "none" : "block",
                    }}
                >
                    <ConversationList
                        ref={ref}
                        authToken={authToken}
                        userId={userId}
                        selectedConversationId={selectedConversation?.id || null}
                        onSelectConversation={handleSelectConversation}
                        showHeader={showHeader}
                        removeActiveMeetingFromSelectedConversation={removeActiveMeetingStatusFromSelectedConversation}
                        onCloseSideChat={handleCloseChat}
                        handleConversation={handleToggleAddConversation}
                        conversationRef={addConversationButtonRef}
                        isSideView={isSideView}
                    />
                </div>
            )}

            {/* --- Chat Window (Right Pane) --- */}
            <div
                className={`chat_wrapper_right ${isChatOpen ? "show_chat" : "hide_chat"}`}
                style={{
                    display: singlePane && !isChatOpen ? "none" : "block",
                }}
            >
                {selectedConversation && currentUser ? (
                    <GlobalChatWindow
                        conversation={selectedConversation}
                        handleChatClose={handleChatClose}
                        singlePane={singlePane}
                        isShowCallingIcon={!selectedConversation.channel}
                    />
                ) : (
                    <div className="start_chat_read" style={{ backgroundColor: "white" }}>
                        <div className="start_chat_read_img">
                            <img src={readchatimg} alt="readchatimg" />
                        </div>
                        <div className="start_chat_read_txt">
                            <h3>Select a conversation</h3>
                            <p>Nothing is selected</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});

export default ChatPage;